import React, { useCallback, useState } from 'react';
import { PlayIcon } from 'lucide-react';
import { VIDEO } from '../../data/dgpt';
import { trackEvent } from '../../utils/analytics';
import { VideoPlayer } from './VideoPlayer';
import { VideoLightbox } from './VideoLightbox';
import { useVimeoPoster } from '../../hooks/useVimeoPoster';
type Mode = 'poster' | 'inline' | 'lightbox';
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * Lightweight video facade. Before Play: a high-resolution poster plus our own
 * UI only — no Vimeo player iframe or scripts. On Play: desktop opens a large
 * lightbox; smaller screens play inline at native 16:9.
 */
export function VideoFacade() {
  const [mode, setMode] = useState<Mode>('poster');
  const [posterFailed, setPosterFailed] = useState(false);
  const poster = useVimeoPoster();
  const handlePlay = () => {
    trackEvent('hero_video_play', {
      video_id: VIDEO.id
    });
    const desktop = window.matchMedia(DESKTOP_QUERY).matches;
    setMode(desktop ? 'lightbox' : 'inline');
  };
  const closeLightbox = useCallback(() => setMode('poster'), []);
  if (mode === 'inline') return <VideoPlayer data-id="element-394" data-mp="3unuclildswda" />;
  return <>
      <button type="button" onClick={handlePlay} aria-label="Play video: DiligenceGPT™: Venture Capital" className="group relative block w-full overflow-hidden rounded-2xl border border-hairline bg-[#0A0A0A] text-left shadow-[0_24px_60px_-36px_rgba(32,22,95,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-ink" style={{
      aspectRatio: '16 / 9',
      height: 'auto'
    }} data-id="element-395" data-mp="3uovrxz9fk50t">
        {poster && !posterFailed ? <img src={poster} onError={() => setPosterFailed(true)} alt="" width={VIDEO.posterWidth} height={VIDEO.posterHeight} {...{
        fetchpriority: 'high'
      } as Record<string, string>} decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-396" data-mp="r6lyn9trygnu" /> : null}

        {/* Light scrim so the play button reads — poster state only, never over the player. */}
        <span className="absolute inset-0 bg-[#0A0A0A]/10 transition-colors duration-200 group-hover:bg-[#0A0A0A]/5" aria-hidden="true" data-id="element-397" data-mp="10yi8e5rtdhft" />

        <span className="absolute inset-0 flex items-center justify-center" data-id="element-398" data-mp="1av4s2r8em2g9">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-lime shadow-lg ring-8 ring-white/20 transition-shadow duration-200 group-hover:ring-white/30 sm:h-[72px] sm:w-[72px]" data-id="element-399" data-mp="1ew6t9dlvsokv">
            <PlayIcon className="ml-1 h-7 w-7 fill-current" aria-hidden="true" data-id="element-400" data-mp="1om9s3ufoowr4" />
          </span>
        </span>
      </button>

      {mode === 'lightbox' ? <VideoLightbox onClose={closeLightbox} data-id="element-401" data-mp="1tkjo2798g1ds" /> : null}
    </>;
}